/**
 * Universal Chat Context
 * ──────────────────────
 * Manages: conversations list, active conversation, messages by convo id,
 * typing indicators, online presence. Sends via socket (REST fallback) and
 * applies optimistic updates so the sender sees their own message instantly,
 * regardless of socket room membership.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import {
  connectSocket,
  disconnectSocket,
} from '../socket/socketClient';
import { messagesApi } from '../api/messagesApi';

const ChatContext = createContext();
export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState({}); // { [convId]: Message[] (oldest -> newest) }
  const [typingUsers, setTypingUsers] = useState({}); // { [convId]: Set<userId> }
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [supportAlerts, setSupportAlerts] = useState([]); // admin-only new-chat toasts

  const socketRef = useRef(null);
  const typingTimersRef = useRef({});
  const activeIdRef = useRef(null);

  useEffect(() => {
    activeIdRef.current = activeConversationId;
  }, [activeConversationId]);

  // ── Helpers (used both by socket events and optimistic updates) ──
  const upsertMessage = useCallback((convId, message) => {
    setMessages((prev) => {
      const existing = prev[convId] || [];
      const incomingId = String(message._id);
      if (existing.some((m) => String(m._id) === incomingId)) {
        
        return prev;
      }
      return { ...prev, [convId]: [...existing, message] };
    });
  }, []);

  const bumpConversation = useCallback(
    (convId, lastMessage, { resetUnread = false, bumpUnread = false } = {}) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c._id === convId);
        if (idx === -1) return prev;
        const current = prev[idx];
        let unreadCount = current.unreadCount || 0;
        if (resetUnread) unreadCount = 0;
        else if (bumpUnread) unreadCount += 1;
        const updated = {
          ...current,
          lastMessage,
          updatedAt: lastMessage?.sentAt || current.updatedAt,
          unreadCount,
        };
        const without = prev.filter((_, i) => i !== idx);
        return [updated, ...without];
      });
    },
    []
  );

  // ── Fetch conversations sidebar ──────────────────────────────────
  const refreshConversations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await messagesApi.listConversations();
      setConversations(res.data?.data || []);
    } catch (err) {
      console.error('[chat] refreshConversations:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load messages for a conversation (initial or pagination) ─────
  const loadMessages = useCallback(async (conversationId, opts = {}) => {
    try {
      const res = await messagesApi.getMessages(conversationId, opts);
      const fetched = res.data?.data || [];
      setMessages((prev) => {
        if (opts.before) {
          const existing = prev[conversationId] || [];
          return { ...prev, [conversationId]: [...fetched, ...existing] };
        }
        return { ...prev, [conversationId]: fetched };
      });
      return fetched;
    } catch (err) {
      console.error('[chat] loadMessages:', err.message);
      return [];
    }
  }, []);

  // ── Connect socket on auth (StrictMode-safe — reuses connection) ─
  useEffect(() => {
    if (!isAuthenticated || !user?._id) {
      // Logout: fully disconnect.
      disconnectSocket();
      socketRef.current = null;
      setConnected(false);
      return;
    }

    const sock = connectSocket(user._id);
    if (!sock) return;
    socketRef.current = sock;

    const onConnect = () => {
      
      setConnected(true);
    };
    const onDisconnect = () => setConnected(false);

    const onMessageNew = (message) => {
      
      const convId = String(message.conversation);

      upsertMessage(convId, message);

      const senderIsMe = String(message.sender?._id) === String(user._id);
      const isActive = convId === activeIdRef.current;

      // Update sidebar (also handles the case where convo isn't in list yet).
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c._id === convId);
        if (idx === -1) {
          refreshConversations();
          return prev;
        }
        const current = prev[idx];
        const updated = {
          ...current,
          lastMessage: {
            content: message.content,
            sender: message.sender?._id,
            sentAt: message.createdAt,
            type: message.type,
          },
          updatedAt: message.createdAt,
          unreadCount: senderIsMe || isActive
            ? 0
            : (current.unreadCount || 0) + 1,
        };
        const without = prev.filter((_, i) => i !== idx);
        return [updated, ...without];
      });

      // Auto-mark as read if convo is currently open.
      if (isActive && !senderIsMe) {
        sock.emit('conversation:read', { conversationId: convId });
      }
    };

    const onConversationNew = (convo) => {
      console.log('[chat] conversation:new', convo?._id);
      setConversations((prev) => {
        if (prev.some((c) => c._id === convo._id)) return prev;
        return [convo, ...prev];
      });
      sock.emit('conversation:join', { conversationId: convo._id });
    };

    const onConversationUpdated = ({ conversationId, lastMessage, unreadFor }) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c._id === conversationId);
        if (idx === -1) return prev;
        const updated = {
          ...prev[idx],
          lastMessage,
          unreadCount: unreadFor,
          updatedAt: lastMessage?.sentAt || prev[idx].updatedAt,
        };
        const without = prev.filter((_, i) => i !== idx);
        return [updated, ...without];
      });
    };

    const onTypingStart = ({ conversationId, userId: typerId }) => {
      setTypingUsers((prev) => {
        const next = { ...prev };
        const set = new Set(next[conversationId] || []);
        set.add(typerId);
        next[conversationId] = set;
        return next;
      });
    };

    const onTypingStop = ({ conversationId, userId: typerId }) => {
      setTypingUsers((prev) => {
        const next = { ...prev };
        const set = new Set(next[conversationId] || []);
        set.delete(typerId);
        next[conversationId] = set;
        return next;
      });
    };

    const onPresenceSnapshot = ({ onlineUserIds: ids }) => {
      setOnlineUserIds(new Set(ids));
    };

    const onUserOnline = ({ userId: id }) => {
      setOnlineUserIds((prev) => new Set([...prev, id]));
    };

    const onUserOffline = ({ userId: id }) => {
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    };

    const onConversationRead = ({ conversationId, userId: readerId }) => {
      setMessages((prev) => {
        const list = prev[conversationId];
        if (!list) return prev;
        return {
          ...prev,
          [conversationId]: list.map((m) =>
            m.readBy?.some((id) => String(id) === String(readerId))
              ? m
              : { ...m, readBy: [...(m.readBy || []), readerId] }
          ),
        };
      });
    };

    // ── ADDED: new guest support chat → admin toast ────────────────────────
    // Only admins receive `support:new` from the server, but we guard anyway.
    const onSupportNew = (payload) => {
      setSupportAlerts((prev) => {
        if (prev.some((a) => a.conversationId === payload.conversationId)) {
          return prev;
        }
        return [{ ...payload }, ...prev].slice(0, 5);
      });
    };

    sock.on('connect', onConnect);
    sock.on('disconnect', onDisconnect);
    sock.on('message:new', onMessageNew);
    sock.on('conversation:new', onConversationNew);
    sock.on('conversation:updated', onConversationUpdated);
    sock.on('conversation:read', onConversationRead);
    sock.on('typing:start', onTypingStart);
    sock.on('typing:stop', onTypingStop);
    sock.on('presence:snapshot', onPresenceSnapshot);
    sock.on('user:online', onUserOnline);
    sock.on('user:offline', onUserOffline);
    sock.on('support:new', onSupportNew); // ── ADDED

    if (sock.connected) setConnected(true);

    refreshConversations();

    return () => {
      // StrictMode-safe: only detach handlers. Socket stays connected.
      // It gets fully disconnected only on auth change (handled above).
      sock.off('connect', onConnect);
      sock.off('disconnect', onDisconnect);
      sock.off('message:new', onMessageNew);
      sock.off('conversation:new', onConversationNew);
      sock.off('conversation:updated', onConversationUpdated);
      sock.off('conversation:read', onConversationRead);
      sock.off('typing:start', onTypingStart);
      sock.off('typing:stop', onTypingStop);
      sock.off('presence:snapshot', onPresenceSnapshot);
      sock.off('user:online', onUserOnline);
      sock.off('user:offline', onUserOffline);
      sock.off('support:new', onSupportNew); // ── ADDED
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?._id]);

  // ── Set active conversation (loads messages, marks as read) ──────
  const setActiveConversation = useCallback(
    async (conversationId) => {
      setActiveConversationId(conversationId);
      if (!conversationId) return;

      // ── ADDED: join the room so we receive realtime typing for this
      //    conversation (guest support typing is relayed to the conv room).
      socketRef.current?.emit('conversation:join', { conversationId });

      if (!messages[conversationId]) {
        await loadMessages(conversationId);
      }

      try {
        await messagesApi.markRead(conversationId);
        socketRef.current?.emit('conversation:read', { conversationId });
        setConversations((prev) =>
          prev.map((c) =>
            c._id === conversationId ? { ...c, unreadCount: 0 } : c
          )
        );
      } catch {
        /* noop */
      }
    },
    [messages, loadMessages]
  );

  // ── Send message (with optimistic state update + idempotency) ──────
  const sendMessage = useCallback(
    (conversationId, content, type = 'text', attachments = []) => {
      // Unique ID per send — prevents server-side duplicate creates
      // (StrictMode dual-mount, retries, double-clicks, etc).
      const clientMessageId =
        (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      return new Promise((resolve) => {
        // Apply optimistic state on success — handles the case where the
        // sender isn't in the conv room or the broadcast hasn't arrived.
        const handleSuccess = (data) => {
          if (!data) return;
          upsertMessage(conversationId, data);
          bumpConversation(
            conversationId,
            {
              content: data.content,
              sender: data.sender?._id,
              sentAt: data.createdAt,
              type: data.type,
            },
            { resetUnread: true }
          );
        };

        const sock = socketRef.current;
        if (!sock || !sock.connected) {
          console.warn('[chat] socket not connected — using REST fallback');
          messagesApi
            .sendMessage(conversationId, {
              content,
              type,
              attachments,
              clientMessageId,
            })
            .then((res) => {
              const data = res.data?.data;
              handleSuccess(data);
              resolve({ ok: true, data });
            })
            .catch((err) => resolve({ ok: false, error: err.message }));
          return;
        }

        sock.emit(
          'message:send',
          { conversationId, content, type, attachments, clientMessageId },
          (response) => {
           
            if (response?.ok && response.data) {
              handleSuccess(response.data);
            }
            resolve(response || { ok: false, error: 'No response' });
          }
        );
      });
    },
    [upsertMessage, bumpConversation]
  );

  // ── Typing indicators (auto-stop after 3s) ───────────────────────
  const startTyping = useCallback((conversationId) => {
    if (!conversationId) return;
    socketRef.current?.emit('typing:start', { conversationId });
    clearTimeout(typingTimersRef.current[conversationId]);
    typingTimersRef.current[conversationId] = setTimeout(() => {
      socketRef.current?.emit('typing:stop', { conversationId });
    }, 3000);
  }, []);

  const stopTyping = useCallback((conversationId) => {
    if (!conversationId) return;
    socketRef.current?.emit('typing:stop', { conversationId });
    clearTimeout(typingTimersRef.current[conversationId]);
  }, []);

  // ── ADDED: dismiss a support-alert toast (admin Messages page) ─────
  const dismissSupportAlert = useCallback((conversationId) => {
    setSupportAlerts((prev) =>
      prev.filter((a) => a.conversationId !== conversationId)
    );
  }, []);

  // ── Find or create a conversation ────────────────────────────────
  const findOrCreateConversation = useCallback(
    async ({ participants, context, isGroup, name }) => {
      try {
        const res = await messagesApi.findOrCreate({
          participants,
          context,
          isGroup,
          name,
        });
        const convo = res.data?.data;
        if (convo) {
          setConversations((prev) => {
            if (prev.some((c) => c._id === convo._id)) return prev;
            return [convo, ...prev];
          });
          socketRef.current?.emit('conversation:join', {
            conversationId: convo._id,
          });
        }
        return { ok: true, data: convo };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },
    []
  );

  const activeConversation = conversations.find(
    (c) => c._id === activeConversationId
  );

  const value = {
    // state
    conversations,
    activeConversationId,
    activeConversation,
    messages,
    typingUsers,
    onlineUserIds,
    loading,
    connected,
    supportAlerts, // ── ADDED
    currentUserId: user?._id,
    currentUserRole: user?.role,
    // actions
    setActiveConversation,
    sendMessage,
    startTyping,
    stopTyping,
    refreshConversations,
    loadMessages,
    findOrCreateConversation,
    dismissSupportAlert, // ── ADDED
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};